import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAddressCard,
  faEye,
  faFileExcel,
  faFilterCircleXmark,
  faHand,
  faInbox,
  faPenToSquare,
  faUser,
  faUserMinus,
} from "@fortawesome/free-solid-svg-icons";

import { crmPostventaIpcApi } from "./crmPostventaIpcApi";
import {
  EMPTY_FILTERS,
  KPIS,
  PAGE_SIZE,
  VISTAS,
  buildExportQuery,
  buildListQuery,
  countActiveFilters,
  kpiLabel,
  resetViewFilters,
} from "./ipcFilters";
import {
  EMPTY_ACTIVAS,
  EMPTY_FINALIZADAS,
  SECCION_TODAS,
  isMine,
  isOpenGestion,
  motivoLabel,
  rowKey,
  seccionDeGestion,
} from "./ipcFormat";
import { demoraDays, formatDate, formatGestiones, todayIso } from "../crmpostventa-shared/crmFormat";
import {
  fichaHref,
  gestionHref,
  isPlainClick,
  readClienteIdParam,
  readGestionIdParam,
  readNuevaGestionParams,
} from "../crmpostventa-shared/crmLinks";
import { saveBlob } from "../crmpostventa-shared/saveBlob";
import useIsMobile from "../crmpostventa-shared/useIsMobile";
import KpiCard from "../crmpostventa-shared/components/KpiCard";
import SegmentedControl, { segmentId } from "../crmpostventa-shared/components/SegmentedControl";
import EmptyState, { LoadError } from "../crmpostventa-shared/components/EmptyState";
import QueueSkeleton from "../crmpostventa-shared/components/Skeleton";
import Pagination from "../crmpostventa-shared/components/Pagination";
import Alert from "../crmpostventa-shared/components/Alert";
import RowMenu from "../crmpostventa-shared/components/RowMenu";
import SectionTabs from "./components/SectionTabs";
import NuevaGestionButton from "./components/NuevaGestionButton";
import IpcFilterBar from "./components/IpcFilterBar";
import { IpcCards, IpcTable, columnsFor } from "./components/IpcQueue";
import { HistoricoChip } from "./components/ipcCells";
import NuevaGestionIpcModal from "./nuevaGestion/NuevaGestionIpcModal";
import GestionarIpcModal from "./gestionar/GestionarIpcModal";
import DetalleIpcModal from "./detalle/DetalleIpcModal";
import AsignarGestionDialog from "./gestionar/AsignarGestionDialog";
import { createCorrelationId } from "./gestionar/gestionarIpcModel";
import "../crmpostventa-rollo/rollo.css";
import "./ipc.css";

const SEARCH_DEBOUNCE_MS = 300;
const RESULTS_ID = "crm-ipc-results";
const TABS_ID = "crm-ipc-vista";
const SWITCH_HELP_ID = "crm-ipc-historico-help";
const SWAL_TITLE = "Gestiones IPC";
// The new row keeps its highlight this long (design Prompt 1).
const SPOTLIGHT_MS = 8000;
const TOAST_MS = 5000;

export const NO_MANAGE_TITLE = "Tu usuario solo puede ver las gestiones IPC";
const HISTORICO_DISABLED_TEXT = "El histórico todavía no está habilitado";

const toast = (title, text) =>
  Swal.fire({
    toast: true,
    position: "bottom-end",
    icon: "success",
    title,
    text,
    showConfirmButton: false,
    showCloseButton: true,
    timer: TOAST_MS,
  });

// Same text as Rollo for a file the server cut at its row limit.
const truncationMessage = ({ rows, total }) =>
  rows === null || rows === undefined
    ? `Se exportó una parte de las ${total} gestiones. Ajustá los filtros para exportar el resto.`
    : `Se exportaron las primeras ${rows} de ${total} gestiones. Ajustá los filtros para exportar el resto.`;

const fallbackExportName = () => `Gestiones_IPC_Finalizadas_${todayIso().replace(/-/g, "")}.xlsx`;

/**
 * Gestiones IPC queue of CRM Postventa › Operaciones › Gestión (design Prompt 1, Prompt 6 and Prompt 7): header with
 * "Nueva gestión", sections with their active counters, KPIs that filter, Activas | Finalizadas, filters, table or
 * cards and pagination. Finalizadas adds the "Incluir históricas" switch (S10) and "Exportar Excel".
 *
 * "Nueva gestión" opens NuevaGestionIpcModal (T4, Prompts 2 and 3); after a creation the new row shows highlighted in
 * the section of its type. `locationSearch` (the page query string) with ?cliente=&nueva=ipc|proactiva&tipo=&terminal=
 * ("Registrar gestión" in the Ficha, Prompt 7) opens the alta in step 2 with that comercio, IPC type and terminal once
 * the queue's first answer confirms the user may manage gestiones (usuarioActual.puedeGestionar), and the panel
 * replaces the URL without the alta either way, so a reload or a later visit does not open it again.
 *
 * "Gestionar" opens GestionarIpcModal (T5, Prompts 4, 6 and 7), and so does ?gestion=<id> ("Volver a la gestión" from
 * the Ficha), which the panel then drops from the URL. `onGestionar(row)` is still told about the row. "Ver detalle"
 * (row menu, closed rows, Gestionar of a closed gestión) opens DetalleIpcModal (T6, Prompts 5, 6 and 7), and so does
 * "Ver gestión" of the similar-gestión warning in the alta; `onVerDetalle(row)` is still told about the row. From the
 * Detalle, "Gestionar" opens Gestionar and "Ver todas" shows the queue in Todas searched by the comercio's CUIT
 * (Activas when the comercio has an open gestión). "Ver ficha" is a real link built by the common link helper;
 * `onNavigate(href, options)` lets the app navigate without a page reload. Tomar, Liberar and Asignar… (the designed
 * assign dialog) call the API directly (D25) and only show when usuarioActual.puedeGestionar is true. The server still
 * enforces every rule.
 */
export default function IpcPanel({ darkMode = false, locationSearch = "", onGestionar, onVerDetalle, onNavigate }) {
  const isMobile = useIsMobile();
  const [seccion, setSeccion] = useState(SECCION_TODAS);
  const [vista, setVista] = useState(VISTAS.ACTIVAS);
  const [searchValue, setSearchValue] = useState("");
  const [texto, setTexto] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [incluirHistorico, setIncluirHistorico] = useState(true);
  const [pagina, setPagina] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [menuKey, setMenuKey] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  // spotlight: row highlighted for a while after it was created ({ id, isNew }).
  const [spotlight, setSpotlight] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportNotice, setExportNotice] = useState(null);
  // Open alta: { categoria, tipo, start } (start: { clienteId, numeroTerminal } when it comes from the Ficha).
  const [nueva, setNueva] = useState(null);
  // Alta asked for by the URL, waiting for the queue's answer that tells whether the user may manage gestiones.
  const [pendingNueva, setPendingNueva] = useState(null);
  // Open Gestionar: { id, row } (row is null when it comes from ?gestion=).
  const [gestionar, setGestionar] = useState(null);
  // Open Detalle: { id, row } (row is null when it comes from "Ver gestión" of the alta; a histórico always has it).
  const [detalle, setDetalle] = useState(null);
  // Row whose "Asignar…" dialog is open.
  const [assignTarget, setAssignTarget] = useState(null);
  const requestRef = useRef(0);
  const mountedRef = useRef(true);
  const resultsRef = useRef(null);
  const nuevaButtonRef = useRef(null);
  const navigateRef = useRef(onNavigate);
  // Attempt (operation, row and version) and correlationId of the last Tomar / Liberar from the row menu.
  const ownershipAttemptRef = useRef(null);
  navigateRef.current = onNavigate;

  const query = useMemo(
    () => buildListQuery({ vista, seccion, texto, filters, incluirHistorico, pagina }),
    [vista, seccion, texto, filters, incluirHistorico, pagina]
  );

  // Only the latest request may update the screen: a slower older response never overwrites a newer one.
  const load = useCallback(async () => {
    requestRef.current += 1;
    const requestId = requestRef.current;
    setLoading(true);
    setError(null);
    try {
      const response = await crmPostventaIpcApi.listarGestiones(query);
      if (requestId !== requestRef.current) return;
      setResult({ vista: query.vista, seccion: query.seccion, response: response || {} });
    } catch (loadError) {
      if (requestId !== requestRef.current) return;
      setError(loadError);
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [query]);

  // reloadKey forces a refresh with the same query (after creating a gestión).
  useEffect(() => {
    load();
  }, [load, reloadKey]);

  useEffect(
    () => () => {
      mountedRef.current = false;
      requestRef.current += 1;
    },
    []
  );

  // "Registrar gestión" in the Ficha links here with the alta in the URL: keep it until the permissions are known (see
  // below) and drop it from the URL, so it is handled once.
  useEffect(() => {
    const request = readNuevaGestionParams(locationSearch);
    if (!request) return;
    setPendingNueva({
      categoria: request.categoria,
      tipo: request.tipo,
      start: { clienteId: request.clienteId, numeroTerminal: request.numeroTerminal },
    });
    navigateRef.current?.(gestionHref({ clienteId: request.clienteId }), { replace: true });
  }, [locationSearch]);

  // "Volver a la gestión" in the Ficha links here with ?gestion=<id>: open its Gestionar and drop it from the URL, so a
  // reload or a later visit does not open it again. Gestionar itself is read-only without manage permission.
  useEffect(() => {
    if (readNuevaGestionParams(locationSearch)) return;
    const id = readGestionIdParam(locationSearch);
    if (!id) return;
    setGestionar({ id, row: null });
    navigateRef.current?.(gestionHref({ clienteId: readClienteIdParam(locationSearch) }), { replace: true });
  }, [locationSearch]);

  // A new query makes the warning of a previous export stale.
  useEffect(() => {
    setExportNotice(null);
  }, [query]);

  useEffect(() => {
    const next = searchValue.trim();
    if (next === texto) return undefined;
    const handle = setTimeout(() => {
      setTexto(next);
      setPagina(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchValue, texto]);

  useEffect(() => {
    if (!spotlight) return undefined;
    const handle = setTimeout(() => setSpotlight(null), SPOTLIGHT_MS);
    return () => clearTimeout(handle);
  }, [spotlight]);

  // A page can empty after an action or a change made by someone else: go back to the last page that exists.
  useEffect(() => {
    if (loading || !result || result.vista !== vista) return;
    const size = Number(result.response?.tamanoPagina) || PAGE_SIZE;
    const lastPage = Math.max(1, Math.ceil((Number(result.response?.total) || 0) / size));
    if (pagina > lastPage) setPagina(lastPage);
  }, [loading, result, vista, pagina]);

  const closeMenu = useCallback(() => setMenuKey(null), []);

  const changeSeccion = (next) => {
    if (next === seccion) return;
    setSeccion(next);
    setFilters((current) => ({ ...current, motivo: "" }));
    setPagina(1);
    setMenuKey(null);
  };

  const changeVista = (next) => {
    if (next === vista) return;
    setVista(next);
    setFilters((current) => resetViewFilters(current));
    setPagina(1);
    setMenuKey(null);
  };

  const changeFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setPagina(1);
  };

  // A KPI applies its filter to Activas; clicking it again, or "Activas", removes it.
  const toggleKpi = (param) => {
    if (vista !== VISTAS.ACTIVAS) {
      setVista(VISTAS.ACTIVAS);
      setFilters((current) => ({ ...resetViewFilters(current), kpi: param || "" }));
    } else {
      setFilters((current) => ({ ...current, kpi: !param || current.kpi === param ? "" : param }));
    }
    setPagina(1);
    setMenuKey(null);
  };

  const clearFilters = () => {
    setSearchValue("");
    setTexto("");
    setFilters(EMPTY_FILTERS);
    setPagina(1);
  };

  const changePage = (next) => {
    setMenuKey(null);
    setPagina(next);
  };

  const response = result?.response;
  const responsables = response?.responsables || [];
  const usuarioActual = response?.usuarioActual || null;
  const currentUserId = usuarioActual?.usuarioId ?? null;
  // Unknown user (older server): keep the controls and let the server answer.
  const canManage = usuarioActual ? usuarioActual.puedeGestionar !== false : true;
  const historicoHabilitado = response ? response.historicoHabilitado !== false : true;

  // The alta of the URL opens only for a user who may manage gestiones; without that permission it is dropped, like
  // the disabled "Nueva gestión" button. It waits for a queue answer, which carries usuarioActual.
  useEffect(() => {
    if (!pendingNueva || !response) return;
    setPendingNueva(null);
    if (canManage) setNueva(pendingNueva);
  }, [pendingNueva, response, canManage]);

  // Shows the queue on page 1 of a section and view with only `texto` as search (no filters), and highlights a row.
  const showInQueue = ({ seccion: nextSeccion, vista: nextVista, texto: nextTexto = "", spotlight: nextSpotlight = null }) => {
    setNueva(null);
    setSeccion(nextSeccion);
    setVista(nextVista);
    setFilters(EMPTY_FILTERS);
    setSearchValue(nextTexto);
    setTexto(nextTexto);
    setPagina(1);
    setMenuKey(null);
    setSpotlight(nextSpotlight);
  };

  // After creating: toast, then page 1 of the section of its type (Prompts 2 and 3) without filters or search, in
  // Activas or, for a gestión registered as resolved, in Finalizadas; refresh, and the new row is highlighted as
  // "Nueva" (first in Activas).
  const handleCreated = ({ gestionId, categoria, tipo, finalizada = false, toast: message } = {}) => {
    if (message) toast(message.title, message.text);
    showInQueue({
      seccion: seccionDeGestion({ categoria, tipo }),
      vista: finalizada ? VISTAS.FINALIZADAS : VISTAS.ACTIVAS,
      spotlight: gestionId ? { id: gestionId, isNew: true } : null,
    });
    setReloadKey((key) => key + 1);
  };

  // "Ver gestión" of the similar-gestión warning: closes the alta and opens the Detalle of that open gestión.
  const handleViewOpen = ({ gestion } = {}) => {
    setNueva(null);
    if (gestion?.id) setDetalle({ id: gestion.id, row: null });
  };

  const handleNueva = (request) => setNueva({ ...request, start: null });

  const handleGestionar = (item) => {
    setMenuKey(null);
    setDetalle(null);
    onGestionar?.(item);
    setGestionar({ id: item.id, row: item });
  };

  const handleVerDetalle = (item) => {
    setMenuKey(null);
    setGestionar(null);
    onVerDetalle?.(item);
    setDetalle({ id: item.id, row: item });
  };

  // "Ver todas" of the Detalle: Todas searched by the CUIT, in Activas when the comercio has an open gestión.
  const handleVerTodas = ({ cuit = "", abiertas = false } = {}) => {
    setDetalle(null);
    showInQueue({ seccion: SECCION_TODAS, vista: abiertas ? VISTAS.ACTIVAS : VISTAS.FINALIZADAS, texto: cuit });
  };

  // After Tomar, Liberar or Asignar in the Detalle: toast and a fresh queue (the Detalle reloads itself).
  const handleDetalleUpdated = ({ title, text } = {}) => {
    if (title) toast(title, text);
    load();
  };

  // After a command in Gestionar: toast and a fresh queue; Finalizar also closes it.
  const handleGestionUpdated = ({ title, text, closed = false } = {}) => {
    if (title) toast(title, text);
    if (closed) setGestionar(null);
    load();
  };

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    setExportNotice(null);
    try {
      const exported = await crmPostventaIpcApi.exportarFinalizadas(buildExportQuery({ seccion, texto, filters, incluirHistorico }));
      if (!mountedRef.current) return;
      saveBlob(exported.blob, exported.fileName || fallbackExportName());
      const matchingTotal = exported.total ?? (Number(result?.response?.total) || 0);
      const exportedRows = exported.rows ?? (exported.truncated ? null : matchingTotal);
      if (exported.truncated) setExportNotice(truncationMessage({ rows: exportedRows, total: matchingTotal }));
      toast("Exportación lista", exportedRows === null ? undefined : `${formatGestiones(exportedRows)} con los filtros aplicados`);
    } catch (exportError) {
      if (!mountedRef.current) return;
      Swal.fire({
        title: "No se pudo exportar",
        text: exportError?.message || "Probá de nuevo en unos segundos.",
        icon: "error",
        confirmButtonText: "Entendido",
      });
    } finally {
      if (mountedRef.current) setExporting(false);
    }
  };

  // One correlationId per attempt of Tomar / Liberar (RN5): the same operation on the same row version reuses it, so
  // repeating it after a network error gets the server's idempotent answer.
  const ownershipCorrelation = (operation, item) => {
    const key = `${operation}:${item.id}:${item.versionToken}`;
    if (ownershipAttemptRef.current?.key !== key) ownershipAttemptRef.current = { key, correlationId: createCorrelationId() };
    return ownershipAttemptRef.current.correlationId;
  };

  // Tomar / Liberar / Asignar: refresh after success; a 409 or 404 means the row changed, so the queue reloads too.
  const runOwnership = async (item, request, success) => {
    setBusyKey(rowKey(item));
    try {
      await request();
      ownershipAttemptRef.current = null;
      toast(success.title, success.text);
      await load();
    } catch (actionError) {
      const conflict = actionError?.status === 409 || actionError?.status === 404;
      Swal.fire({
        title: SWAL_TITLE,
        text: actionError?.message || "No se pudo completar la acción.",
        icon: conflict ? "warning" : "error",
        confirmButtonText: "Entendido",
      });
      if (conflict) await load();
    } finally {
      if (mountedRef.current) setBusyKey(null);
    }
  };

  const handleTomar = (item) =>
    runOwnership(item, () => crmPostventaIpcApi.tomar(item.id, { versionToken: item.versionToken, correlationId: ownershipCorrelation("TOMAR", item) }), {
      title: "Tomaste la gestión",
      text: `${item.nombreComercio} · ${motivoLabel(item)}`,
    });

  const handleLiberar = async (item) => {
    const confirmation = await Swal.fire({
      title: "Liberar gestión",
      text: "La gestión va a quedar sin responsable.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Liberar",
      cancelButtonText: "Cancelar",
    });
    if (!confirmation?.isConfirmed) return;
    await runOwnership(
      item,
      () => crmPostventaIpcApi.liberar(item.id, { versionToken: item.versionToken, correlationId: ownershipCorrelation("LIBERAR", item) }),
      {
        title: "Liberaste la gestión",
        text: `${item.nombreComercio} · queda Sin asignar`,
      }
    );
  };

  // Any advisor who can manage may assign (D25), with the designed dialog; taking a gestión away from another advisor
  // needs a reason, which stays in the timeline. A 409 or 404 means the row changed: warn and reload the queue.
  const handleAsignar = (item) => setAssignTarget(item);

  const submitAssign = async ({ usuarioId, observacion, correlationId, nombre, reasignada }) => {
    const item = assignTarget;
    try {
      await crmPostventaIpcApi.asignar(item.id, { usuarioId, observacion, versionToken: item.versionToken, correlationId });
    } catch (assignError) {
      if (assignError?.status !== 409 && assignError?.status !== 404) throw assignError;
      if (!mountedRef.current) return;
      setAssignTarget(null);
      Swal.fire({ title: SWAL_TITLE, text: assignError?.message || "No se pudo completar la acción.", icon: "warning", confirmButtonText: "Entendido" });
      await load();
      return;
    }
    if (!mountedRef.current) return;
    setAssignTarget(null);
    toast(reasignada ? `Gestión reasignada a ${nombre}` : `Gestión asignada a ${nombre}`, item.nombreComercio);
    await load();
  };

  const fichaItem = (item) => {
    const href = fichaHref({ clienteId: item.clienteId });
    if (!href) {
      return { key: "ficha", label: "Ver ficha", icon: faAddressCard, disabled: true, title: "La gestión no tiene una terminal asociada" };
    }
    return {
      key: "ficha",
      label: "Ver ficha",
      icon: faAddressCard,
      href,
      onSelect: (event) => {
        if (!onNavigate || !isPlainClick(event)) return;
        event.preventDefault();
        onNavigate(href);
      },
    };
  };

  // Row menu (design RowMenu, Prompt 6 and 7): open gestiones offer Ver detalle, Gestionar and Ver ficha, plus Tomar
  // when nobody has it, Liberar when it is yours and Asignar… for anyone who can manage; closed and histórico rows
  // only Ver detalle and Ver ficha.
  const menuItemsFor = (item) => {
    const detalle = { key: "detalle", label: "Ver detalle", icon: faEye, onSelect: () => handleVerDetalle(item) };
    if (!isOpenGestion(item)) return [detalle, fichaItem(item)];

    const assigned = item.responsableUsuarioId !== null && item.responsableUsuarioId !== undefined;
    const ownership = canManage
      ? [
          assigned ? null : { key: "tomar", label: "Tomar", icon: faHand, onSelect: () => handleTomar(item) },
          isMine(item, currentUserId) ? { key: "liberar", label: "Liberar", icon: faUserMinus, onSelect: () => handleLiberar(item) } : null,
          { key: "asignar", label: "Asignar…", icon: faUser, onSelect: () => handleAsignar(item) },
        ].filter(Boolean)
      : [];

    return [
      detalle,
      { key: "gestionar", label: "Gestionar", icon: faPenToSquare, onSelect: () => handleGestionar(item) },
      fichaItem(item),
      ...(ownership.length ? ["separator", ...ownership] : []),
    ];
  };

  const shownVista = result?.vista;
  const finalizadas = shownVista === VISTAS.FINALIZADAS;
  const shownTodas = (result?.seccion ?? seccion) === SECCION_TODAS;

  const renderActions = (item, card = false) => {
    const key = rowKey(item);
    const open = isOpenGestion(item);
    return (
      <div className={card ? "crm-rollo__card-actions" : "crm-rollo__actions"}>
        <button
          type="button"
          className="crm-rollo__btn crm-rollo__btn--secondary crm-rollo__btn--sm"
          disabled={busyKey === key}
          onClick={() => (open ? handleGestionar(item) : handleVerDetalle(item))}
        >
          {open ? "Gestionar" : "Ver detalle"}
        </button>
        <RowMenu
          label={`Más acciones para ${item.nombreComercio || "la gestión"}`}
          open={menuKey === key}
          onToggle={() => setMenuKey((current) => (current === key ? null : key))}
          onClose={closeMenu}
          items={menuItemsFor(item)}
        />
      </div>
    );
  };

  const renderNuevaGestion = (ref) => (
    <NuevaGestionButton
      seccion={seccion}
      disabled={!canManage}
      title={canManage ? undefined : NO_MANAGE_TITLE}
      onSelect={handleNueva}
      buttonRef={ref}
    />
  );

  const today = todayIso();
  const activeCount = countActiveFilters(filters, vista);
  const hasFilters = activeCount > 0 || texto !== "";
  const canClear = activeCount > 0 || searchValue.trim() !== "";
  const isStale = !result || shownVista !== vista;
  const refreshing = loading && !isStale;
  const loadedItems = response?.items || [];
  const total = Number(response?.total) || 0;
  const kpis = response?.kpis;
  const conteos = response?.conteos;
  const diasParaDemora = demoraDays(response?.diasParaDemora);
  const canExport = !isStale && !error && finalizadas && total > 0 && !exporting;
  // The new gestión goes first while it is highlighted and on the loaded page.
  const spotlightRow = spotlight && !finalizadas ? loadedItems.find((item) => !item.esHistorico && item.id === spotlight.id) : null;
  const items = spotlightRow ? [spotlightRow, ...loadedItems.filter((item) => item !== spotlightRow)] : loadedItems;
  const corte = response?.fechaCorteHistorico;
  const showCorte = finalizadas && historicoHabilitado && corte && items.some((item) => item.esHistorico);

  useEffect(() => {
    if (!spotlight || loading) return;
    const row = resultsRef.current?.querySelector(".is-spotlight");
    if (row && typeof row.scrollIntoView === "function") row.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [spotlight, loading, items.length]);

  const renderEmpty = () => {
    if (hasFilters) {
      return (
        <EmptyState
          icon={faFilterCircleXmark}
          title="No se encontraron gestiones con los filtros seleccionados"
          text="Probá con otro comercio, CUIT o número de comercio, o sacá algún filtro."
          action={
            <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary" onClick={clearFilters}>
              Limpiar filtros
            </button>
          }
        />
      );
    }

    if (vista === VISTAS.ACTIVAS) {
      return (
        <EmptyState
          icon={faInbox}
          title={EMPTY_ACTIVAS[seccion]}
          text={
            seccion === "proactiva"
              ? "Registrá una visita comercial u otra acción con un comercio."
              : "Cuando un comercio se comunique, registrá su pedido para seguirlo hasta resolverlo."
          }
          action={renderNuevaGestion()}
        />
      );
    }

    return <EmptyState icon={faInbox} title={EMPTY_FINALIZADAS[seccion]} text="Las gestiones resueltas o descartadas aparecen acá." />;
  };

  const renderResults = () => {
    if (error) {
      return (
        <LoadError
          title="No pudimos cargar las gestiones"
          message="Hubo un problema de conexión con el servidor. Probá de nuevo en unos segundos."
          onRetry={load}
        />
      );
    }
    if (isStale) return <QueueSkeleton columns={columnsFor(vista === VISTAS.FINALIZADAS, seccion === SECCION_TODAS)} isMobile={isMobile} />;
    if (!items.length) return renderEmpty();

    const queueProps = {
      items,
      finalizadas,
      showTipo: shownTodas,
      currentUserId,
      today,
      busyId: busyKey,
      spotlight,
      renderActions,
    };

    return (
      <>
        {showCorte ? (
          <p className="crm-ipc__corte">
            <HistoricoChip />
            <span>{`Registradas en el sistema anterior hasta el ${formatDate(corte)}`}</span>
          </p>
        ) : null}
        {isMobile ? <IpcCards {...queueProps} /> : <IpcTable {...queueProps} />}
      </>
    );
  };

  const activeKpi = vista === VISTAS.ACTIVAS ? kpiLabel(filters.kpi) : "";
  const meta =
    !isStale && !error && total > 0
      ? [
          formatGestiones(total),
          finalizadas ? "" : activeKpi,
          finalizadas ? "cierre más reciente primero" : "vencidas primero, después por prioridad y antigüedad",
        ]
          .filter(Boolean)
          .join(" · ")
      : "";

  return (
    <section
      className={`crm-rollo crm-ipc${darkMode ? " is-dark" : ""}${isMobile ? " is-mobile" : ""}`}
      aria-labelledby="crm-ipc-title"
    >
      <div className="crm-rollo__top">
        <div>
          <h3 id="crm-ipc-title" className="crm-rollo__title">
            Gestiones IPC
          </h3>
          <p className="crm-rollo__desc">Pedidos de los comercios y acciones comerciales del asesor.</p>
        </div>
        {renderNuevaGestion(nuevaButtonRef)}
      </div>

      <SectionTabs value={seccion} counts={response?.secciones} onChange={changeSeccion} />

      {error ? null : (
        <div className="crm-rollo__kpis crm-ipc__kpis">
          {KPIS.map((item) => (
            <KpiCard
              key={item.key}
              label={item.label}
              value={kpis?.[item.key]}
              warn={item.warn}
              title={
                item.warn
                  ? `Más de ${diasParaDemora || 3} días sin ninguna acción, salvo Esperando al comercio`
                  : undefined
              }
              pressed={Boolean(item.param) && vista === VISTAS.ACTIVAS && filters.kpi === item.param}
              onClick={() => toggleKpi(item.param)}
            />
          ))}
        </div>
      )}

      <div className="crm-rollo__bar">
        <SegmentedControl
          label="Ver gestiones"
          idPrefix={TABS_ID}
          controls={RESULTS_ID}
          value={vista}
          onChange={changeVista}
          options={[
            { value: VISTAS.ACTIVAS, label: "Activas", count: conteos?.activas },
            { value: VISTAS.FINALIZADAS, label: "Finalizadas", count: conteos?.finalizadas },
          ]}
        />
        <div className="crm-rollo__bar-end">
          <span className="crm-rollo__meta" aria-live="polite">
            {meta}
          </span>
          {vista === VISTAS.FINALIZADAS ? (
            <>
              <label className={`crm-ipc__switch${historicoHabilitado ? "" : " is-disabled"}`}>
                <input
                  type="checkbox"
                  role="switch"
                  aria-label="Incluir históricas"
                  aria-describedby={historicoHabilitado ? undefined : SWITCH_HELP_ID}
                  checked={historicoHabilitado && incluirHistorico}
                  disabled={!historicoHabilitado}
                  onChange={(event) => {
                    setIncluirHistorico(event.target.checked);
                    setPagina(1);
                  }}
                />
                <span className="crm-ipc__switch-track" aria-hidden="true" />
                <span>Incluir históricas</span>
              </label>
              {historicoHabilitado ? null : (
                <span id={SWITCH_HELP_ID} className="crm-rollo__meta">
                  {HISTORICO_DISABLED_TEXT}
                </span>
              )}
              <button
                type="button"
                className="crm-rollo__btn crm-rollo__btn--secondary crm-rollo__btn--sm"
                disabled={!canExport}
                aria-busy={exporting || undefined}
                onClick={handleExport}
              >
                {exporting ? (
                  <>
                    <span className="crm-rollo__spinner" aria-hidden="true" />
                    Exportando…
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faFileExcel} aria-hidden="true" />
                    Exportar Excel
                  </>
                )}
              </button>
            </>
          ) : null}
        </div>
      </div>

      {pendingNueva ? (
        <Alert tone="info" role="status" title="La nueva gestión se va a abrir cuando se carguen las gestiones.">
          {error ? "Tocá Reintentar para cargarlas." : null}
        </Alert>
      ) : null}

      {exportNotice ? (
        <Alert tone="warn" role="alert" title="Exportación parcial">
          {exportNotice}
        </Alert>
      ) : null}

      {error ? null : (
        <IpcFilterBar
          vista={vista}
          seccion={seccion}
          isMobile={isMobile}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          filters={filters}
          onFilterChange={changeFilter}
          responsables={responsables}
          today={today}
          activeCount={activeCount}
          canClear={canClear}
          onClear={clearFilters}
          panelOpen={panelOpen}
          onTogglePanel={() => setPanelOpen((open) => !open)}
        />
      )}

      <div
        ref={resultsRef}
        id={RESULTS_ID}
        role="tabpanel"
        aria-labelledby={segmentId(TABS_ID, vista)}
        aria-busy={refreshing || undefined}
        className={`crm-rollo__results${refreshing ? " is-refreshing" : ""}`}
      >
        {renderResults()}
      </div>

      {!isStale && !error ? (
        <Pagination
          pagina={pagina}
          tamanoPagina={Number(response?.tamanoPagina) || PAGE_SIZE}
          total={total}
          disabled={loading}
          onChange={changePage}
        />
      ) : null}

      {nueva ? (
        <NuevaGestionIpcModal
          categoria={nueva.categoria}
          tipo={nueva.tipo}
          start={nueva.start}
          darkMode={darkMode}
          asesorNombre={usuarioActual?.nombre}
          returnFocusRef={nueva.start ? undefined : nuevaButtonRef}
          onClose={() => setNueva(null)}
          onCreated={handleCreated}
          onViewOpen={handleViewOpen}
        />
      ) : null}

      {gestionar ? (
        <GestionarIpcModal
          key={gestionar.id}
          gestionId={gestionar.id}
          row={gestionar.row}
          responsables={responsables}
          currentUserId={currentUserId}
          darkMode={darkMode}
          onClose={() => setGestionar(null)}
          onUpdated={handleGestionUpdated}
          onVerDetalle={handleVerDetalle}
          onNavigate={onNavigate}
        />
      ) : null}

      {detalle ? (
        <DetalleIpcModal
          key={`${detalle.row?.esHistorico ? "h" : "g"}-${detalle.id}`}
          gestionId={detalle.id}
          row={detalle.row}
          fechaCorteHistorico={response?.fechaCorteHistorico}
          responsables={responsables}
          currentUserId={currentUserId}
          darkMode={darkMode}
          onClose={() => setDetalle(null)}
          onGestionar={handleGestionar}
          onVerTodas={handleVerTodas}
          onVerOtra={(item) => setDetalle({ id: item.id, row: item.esHistorico ? item : null })}
          onUpdated={handleDetalleUpdated}
          onNavigate={onNavigate}
        />
      ) : null}

      {assignTarget ? (
        <AsignarGestionDialog
          gestion={assignTarget}
          responsables={responsables}
          currentUserId={currentUserId}
          darkMode={darkMode}
          onClose={() => setAssignTarget(null)}
          onSubmit={submitAssign}
        />
      ) : null}
    </section>
  );
}
